// src/components/ProjectFiles.js - PRO VERSION (UI MEJORADA + DRAG & DROP)
import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Tooltip,
  Alert,
  Snackbar,
  LinearProgress,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Download,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import api from '../services/api';
import { openStoredFile } from '../services/fileAccessService';

export default function ProjectFiles({
  projectId,
  files = [],
  onFileChange,
  attachmentCategory = 'general',
  title = 'Project Documents',
  description,
  buttonLabel = 'Upload File',
  helperText = 'Soporta PDF, Imagenes, Excel o XML (Max 10MB)',
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.xml'
}) {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // Estado para el efecto visual de arrastrar
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const fileInputRef = useRef(null);

  // --- LOGICA DE UPLOAD REUTILIZABLE ---
  const uploadFile = async (file) => {
    if (!file) return;

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('⚠️ The file is too large (maximum 10 MB).');
      return;
    }

    const formData = new FormData();
    formData.append('attachments', file);
    formData.append('attachment_category', attachmentCategory);

    setIsUploading(true);
    setError('');
    setSuccessMsg('');

    try {
      console.log(`📤 Subiendo: ${file.name}`);
      const response = await api.put(`/projects/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setSuccessMsg(`✅ ${file.name} subido correctamente`);
        if (onFileChange) onFileChange();
      }
    } catch (err) {
      console.error('❌ Error upload:', err);
      setError('The file could not be uploaded. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // --- HANDLERS (INPUT & DRAG DROP) ---

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0]; // Tomamos el primer archivo
    uploadFile(file);
  };

  const handleDownload = async (file) => {
    try {
      await openStoredFile(file.path, 'inline');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'The file could not be opened.');
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Permanently delete "${file.originalname}"?`)) return;

    try {
      const response = await api.delete(`/projects/${projectId}/files/${file.filename}`);
      if (response.data.success) {
        setSuccessMsg('File deleted successfully');
        if (onFileChange) onFileChange();
      }
    } catch (err) {
      setError('The file could not be deleted.');
    }
  };

  // --- HELPERS VISUALES ---

  const getFileIconInfo = (filename) => {
    if (!filename) return { icon: <InsertDriveFile />, color: theme.palette.grey[500], bg: theme.palette.grey[100] };
    const ext = filename.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) 
      return { icon: <ImageIcon />, color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1) };
    
    if (['pdf'].includes(ext)) 
      return { icon: <PdfIcon />, color: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.1) };
    
    if (['xls', 'xlsx', 'csv'].includes(ext)) 
      return { icon: <ExcelIcon />, color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.1) };
    
    if (['doc', 'docx', 'txt'].includes(ext)) 
      return { icon: <DocIcon />, color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.1) };

    return { icon: <InsertDriveFile />, color: theme.palette.text.secondary, bg: theme.palette.action.hover };
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
  };

  return (
    <Paper 
      elevation={0} 
      variant="outlined" 
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        p: 0,
        mt: { xs: 1.5, sm: 3 },
        borderRadius: 3,
        overflow: 'hidden', 
        border: `1px solid ${theme.palette.divider}` 
      }}
    >
      {/* HEADER */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.default }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {description || `Gestiona los adjuntos y recursos (${files?.length || 0}) · ${helperText}`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current.click()}
            disabled={isUploading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 2, alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
          >
            {buttonLabel}
          </Button>
        </Box>
      </Box>

      {/* ZONA DE CARGA (DRAG & DROP) Y ALERTAS */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
        {/* Alertas */}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        
        {/* Zona Drag & Drop */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 3,
            bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
            p: { xs: 2, sm: 3, md: 4 },
            minHeight: { xs: 170, sm: 210 },
            width: '100%',
            minWidth: 0,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            mb: 3,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
          onClick={() => fileInputRef.current.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={accept}
            style={{ display: 'none' }}
          />
          
          {isUploading ? (
            <Box sx={{ maxWidth: 300, mx: 'auto' }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>Subiendo archivo...</Typography>
              <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'text.secondary', mb: 1 }} />
              <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ overflowWrap: 'anywhere' }}>
                {isDragging ? 'Drop the file here!' : 'Click or drag a file here'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                Supports PDF, images, and Excel files (maximum 10 MB)
              </Typography>
            </>
          )}
        </Box>

        {/* LISTA DE ARCHIVOS */}
        {(!files || files.length === 0) ? (
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary">There are no attachments yet.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {files.map((file, index) => {
              const { icon, color, bg } = getFileIconInfo(file.originalname);
              
              return (
                <ListItem
                  key={index}
                  sx={{
                    minWidth: 0,
                    px: { xs: 1, sm: 2 },
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mb: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.light,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  {/* Icono del tipo de archivo */}
                  <Avatar variant="rounded" sx={{ bgcolor: bg, color: color, mr: { xs: 1, sm: 2 }, flexShrink: 0 }}>
                    {icon}
                  </Avatar>

                  {/* Info del archivo */}
                  <ListItemText
                    sx={{ minWidth: 0 }}
                    primary={
                      <Typography variant="body2" fontWeight={600} noWrap title={file.originalname}>
                        {file.originalname || "Sin nombre"}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ bgcolor: theme.palette.action.selected, px: 0.8, py: 0.2, borderRadius: 1 }}>
                          {formatBytes(file.size)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • Subido el {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                    }
                  />

                  {/* Acciones */}
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Download / View">
                      <IconButton aria-label={`Open ${file.originalname || 'file'}`} onClick={() => handleDownload(file)} size="small" sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Delete">
                      <IconButton aria-label={`Delete ${file.originalname || 'file'}`} onClick={() => handleDelete(file)} size="small" sx={{ color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Snackbar de éxito */}
      <Snackbar 
        open={!!successMsg} 
        autoHideDuration={4000} 
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMsg('')} severity="success" variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
